import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Checkbox, TextField } from '@soluto-private/mx-asurion-ui-react';

import { breakpoints } from '@service-initiation/shared-ui-breakpoints';
import { formatPhoneNumberInputValue } from '@service-initiation/shared-utils-helpers';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import {
  type CustomerDetails,
  DeliveryMethod,
  useDeliveryMethodValue,
} from '@service-initiation/tech-care-data-access-store';

import { customerDetailsFormSchema } from '../schemas';
import { stripValue } from '../utils';
import { TermsAndConditionsText } from './TermsAndConditionsText';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`;

const NameContainer = styled.div`
  display: flex;
  gap: 12px;
  div {
    width: 100%;
  }
`;

const CheckboxContainer = styled.div`
  ${breakpoints.smallDesktop} {
    margin-bottom: 14px;
  }
`;

const TEXT = TranslationEntries.Repairs.CustomerDetails.Form;

type CustomerDetailsProps = {
  formState: CustomerDetails | undefined;
  setFormState: React.Dispatch<
    React.SetStateAction<CustomerDetails | undefined>
  >;
};

export const CustomerDetailsForm = ({
  formState,
  setFormState,
}: CustomerDetailsProps) => {
  const { t } = useTranslation();
  const deliveryMethod = useDeliveryMethodValue();
  const [errorFields, setErrorFields] = useState({
    agreeToEmailOffers: '',
    customerMessage: '',
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
  });

  const validateForm = ({
    fieldName,
    fieldValue,
  }: {
    fieldName: string;
    fieldValue: unknown;
  }) => {
    const fieldData = { [fieldName]: fieldValue };

    customerDetailsFormSchema(t)
      .validateAt(fieldName, fieldData)
      .then(() => {
        setErrorFields({ ...errorFields, ...{ [fieldName]: '' } });
      })
      .catch((error) => {
        setErrorFields({
          ...errorFields,
          ...{ [fieldName]: error?.errors[0] },
        });
      });
  };

  const handleChange = ({
    target: { name, value },
  }: React.ChangeEvent<Partial<HTMLInputElement>>) => {
    if (name) {
      const strippedValue = value && stripValue(name, value);
      setFormState((currentCustomerDetails) => ({
        ...currentCustomerDetails,
        ...{ [name]: strippedValue },
      }));
    }
  };

  const handleCheckboxChange = ({
    target: { checked, name },
  }: React.ChangeEvent<Partial<HTMLInputElement>>) => {
    if (name) {
      validateForm({ fieldName: name, fieldValue: checked });
      setFormState((currentCustomerDetails) => ({
        ...currentCustomerDetails,
        ...{ [name]: checked },
      }));
    }
  };

  const handleBlur = ({
    target: { checked, name, value },
  }: React.BaseSyntheticEvent<Partial<HTMLInputElement>>) => {
    const fieldValue = name === 'smsOptIn' ? checked : value;
    validateForm({ fieldName: name, fieldValue: fieldValue });
  };

  /* Dispatch error analytics? */

  return (
    <StyledForm data-testid="customer-details-form">
      <NameContainer>
        <TextField
          autoComplete="given-name"
          data-testid="customer-details-form-first-name"
          defaultValue={formState?.firstName}
          fieldStatus={errorFields['firstName'] ? 'error' : 'default'}
          helperText={errorFields['firstName']}
          inputClassName="fs-mask"
          label={t(TEXT.FirstName)}
          name="firstName"
          onBlur={handleBlur}
          onChange={handleChange}
          value={formState?.firstName}
        />
        <TextField
          autoComplete="family-name"
          data-testid="customer-details-form-last-name"
          defaultValue={formState?.lastName}
          fieldStatus={errorFields['lastName'] ? 'error' : 'default'}
          helperText={errorFields['lastName']}
          inputClassName="fs-mask"
          label={t(TEXT.LastName)}
          name="lastName"
          onBlur={handleBlur}
          onChange={handleChange}
          value={formState?.lastName}
        />
      </NameContainer>
      <TextField
        autoComplete="email"
        data-testid="customer-details-form-email"
        defaultValue={formState?.email}
        fieldStatus={errorFields['email'] ? 'error' : 'default'}
        helperText={errorFields['email']}
        inputClassName="fs-mask"
        label={t(TEXT.EmailLabel)}
        name="email"
        onBlur={handleBlur}
        onChange={handleChange}
        value={formState?.email}
      />
      <TextField
        autoComplete="tel"
        data-testid="customer-details-form-phone-number"
        defaultValue={formState?.phoneNumber}
        fieldStatus={errorFields['phoneNumber'] ? 'error' : 'default'}
        helperText={
          errorFields['phoneNumber']
            ? errorFields['phoneNumber']
            : t(TEXT.PhoneNumberDefaultHelperText) || ''
        }
        inputClassName="fs-mask"
        inputMode="tel"
        label={t(TEXT.PhoneNumberLabel)}
        name="phoneNumber"
        onBlur={handleBlur}
        onChange={handleChange}
        value={formatPhoneNumberInputValue(formState?.phoneNumber)}
      />
      {deliveryMethod !== DeliveryMethod.ComeToMe && (
        <div>
          <TermsAndConditionsText />
        </div>
      )}
      <CheckboxContainer>
        <Checkbox
          checked={formState?.agreeToEmailOffers}
          containerProps={{
            'data-testid': 'form-email-offers-checkbox-container',
          }}
          defaultChecked={formState?.agreeToEmailOffers}
          label={t(TEXT.EmailOffers)}
          name="agreeToEmailOffers"
          onBlur={handleBlur}
          onChange={handleCheckboxChange}
          options={{ multiline: false }}
        />
      </CheckboxContainer>
    </StyledForm>
  );
};
